import React, { useEffect, useRef, useState } from 'react';
import { CalendarClock, CheckCircle2, Languages, TriangleAlert } from 'lucide-react';
import { Toaster } from '../../components/ui/sonner';
import '../sales.css';
import { fmtDate } from '../salesI18n';
import { PUB_T, pubUrl } from './publicI18n';
import { PublicCompare } from './PublicCompare';
import { PublicSummary, PublicItems, PublicTotals } from './PublicItems';
import { RepCard, MobileBar, AcceptDialog, InterestDialog } from './PublicActions';

const LOGO = `${process.env.PUBLIC_URL || ''}/sales-logo.png`;

const Header = ({ lang, setLang }) => (
  <header className="flex items-center justify-between gap-3 py-4" data-testid="public-header">
    <div className="flex items-center gap-3">
      <img src={LOGO} alt="PM Home Remodeling" className="h-12 w-auto object-contain drop-shadow-sm" data-testid="public-logo" />
      <div><p className="font-extrabold text-slate-900 leading-tight">PM Home Remodeling</p><p className="text-[11px] text-slate-500">Georgia, USA</p></div>
    </div>
    <button type="button" onClick={() => setLang(lang === 'en' ? 'pt-BR' : 'en')} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/80 border border-white/90 text-xs font-bold text-teal-800 shadow-sm hover:bg-white transition-colors" data-testid="public-lang-toggle">
      <Languages className="h-3.5 w-3.5" /> {lang === 'en' ? 'PT' : 'EN'}
    </button>
  </header>
);

const Hero = ({ d, t, lang }) => (
  <section className="sales-rise" data-testid="public-hero">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">{t.proposal} {d.number}</p>
    <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mt-1 tracking-tight" data-testid="public-title">{t.proposalFor(d.customer.name.split(' ')[0])}</h1>
    {d.customer.address && <p className="text-sm text-slate-600 mt-2">{d.customer.address}</p>}
    <div className="flex flex-wrap gap-2 mt-4">
      <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/80 border border-white/90 text-xs font-semibold text-slate-700"><CalendarClock className="h-3.5 w-3.5 text-teal-700" /> {t.validUntil} {fmtDate(d.valid_until, lang)}</span>
      {d.accepted && <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-teal-600 text-white text-xs font-semibold" data-testid="public-accepted-badge"><CheckCircle2 className="h-3.5 w-3.5" /> {t.accepted}</span>}
      {d.expired && <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-100 border border-amber-200 text-amber-900 text-xs font-semibold" data-testid="public-expired-badge"><TriangleAlert className="h-3.5 w-3.5" /> {t.expired}</span>}
    </div>
    {d.accepted && <p className="mt-3 rounded-2xl bg-teal-50 border border-teal-100 text-teal-900 text-sm px-4 py-3" data-testid="public-accepted-note">{d.accepted.by ? t.acceptedBy(d.accepted.by, fmtDate(d.accepted.at, lang)) : t.accepted} · {t.acceptedHint}</p>}
    {d.expired && <p className="mt-3 rounded-2xl bg-amber-50 border border-amber-100 text-amber-900 text-sm px-4 py-3">{t.expiredHint}</p>}
  </section>
);

const Terms = ({ d, t, lang }) => (
  <section className="space-y-4 text-sm sales-rise sales-rise-4" data-testid="public-terms">
    {d.notes && <div><h3 className="font-bold text-slate-900 mb-1">{t.notes}</h3><p className="text-slate-700 whitespace-pre-wrap">{d.notes}</p></div>}
    <div><h3 className="font-bold text-slate-900 mb-1">{t.payment}</h3><p className="text-slate-700">{d.payment_terms[lang]}</p></div>
    <div><h3 className="font-bold text-slate-900 mb-1">{t.terms}</h3><p className="text-xs text-slate-500 leading-relaxed">{d.terms[lang]}</p></div>
  </section>
);

export default function PublicProposalPage({ token }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [lang, setLang] = useState('pt-BR');
  const [dlg, setDlg] = useState(null);
  const fetched = useRef(false);
  const t = PUB_T[lang];

  useEffect(() => {
    document.documentElement.classList.add('sales-theme');
    if (fetched.current) return undefined;
    fetched.current = true;
    fetch(pubUrl(token, new URLSearchParams(window.location.search).get('preview') ? '?preview=1' : '')).then(async (r) => {
      if (!r.ok) throw new Error('nf');
      const data = await r.json();
      setD(data); setLang(data.lang === 'en' ? 'en' : 'pt-BR');
      document.title = `${PUB_T[data.lang === 'en' ? 'en' : 'pt-BR'].proposal} ${data.number} · PM Home Remodeling`;
    }).catch(() => setErr(true));
    return () => document.documentElement.classList.remove('sales-theme');
  }, [token]);

  if (err) return <div className="min-h-[100dvh] flex items-center justify-center p-6 text-center" data-testid="public-not-found"><div><p className="text-xl font-bold text-slate-900">{t.notFound}</p><p className="text-sm text-slate-600 mt-1">{t.notFoundHint}</p></div></div>;
  if (!d) return <div className="min-h-[100dvh] flex items-center justify-center" data-testid="public-loading"><div className="h-12 w-12 rounded-full bg-teal-600/80 animate-pulse shadow-lg" /></div>;

  const onAccepted = (name) => setD((x) => ({ ...x, status: 'accepted', accepted: { at: new Date().toISOString(), by: name, via: 'online' } }));
  return (
    <div className="min-h-[100dvh] pb-28 lg:pb-10" data-testid="public-proposal-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <Header lang={lang} setLang={setLang} />
        <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-10 mt-4">
          <main className="space-y-10 min-w-0">
            <Hero d={d} t={t} lang={lang} />
            <PublicCompare token={token} before={d.before} variants={d.variants} afterId={d.after_file_id} t={t} />
            <PublicSummary d={d} t={t} />
            <PublicItems d={d} t={t} lang={lang} />
            <div className="lg:hidden"><PublicTotals d={d} t={t} lang={lang} /></div>
            <Terms d={d} t={t} lang={lang} />
            <div className="lg:hidden"><RepCard d={d} t={t} lang={lang} token={token} variant="-mobile" onAccept={() => setDlg('accept')} onInterest={() => setDlg('interest')} /></div>
          </main>
          <aside className="hidden lg:block"><div className="sticky top-6 space-y-4"><PublicTotals d={d} t={t} lang={lang} /><RepCard d={d} t={t} lang={lang} token={token} onAccept={() => setDlg('accept')} onInterest={() => setDlg('interest')} /></div></aside>
        </div>
        <footer className="mt-12 text-center text-[11px] text-slate-500">© {new Date().getFullYear()} PM Home Remodeling · Georgia, USA</footer>
      </div>
      <MobileBar d={d} t={t} onAccept={() => setDlg('accept')} onInterest={() => setDlg('interest')} />
      <AcceptDialog open={dlg === 'accept'} onOpenChange={(o) => !o && setDlg(null)} d={d} t={t} lang={lang} token={token} onDone={onAccepted} />
      <InterestDialog open={dlg === 'interest'} onOpenChange={(o) => !o && setDlg(null)} t={t} token={token} onDone={() => setD((x) => ({ ...x, interest_at: new Date().toISOString() }))} />
      <Toaster richColors position="top-center" closeButton duration={3500} expand visibleToasts={3} />
    </div>
  );
}
